import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * OpenRoad's type scale uses semantic names rather than t-shirt sizes, so
 * tailwind-merge cannot tell `text-body-sm` (a size) from `text-pohutukawa`
 * (a colour) on its own — it files both under `text-color` and drops one.
 * Registering the scale explicitly keeps size and colour in separate conflict
 * groups. The same applies to the named elevation tokens.
 *
 * Keep in step with the `--text-*` and `--shadow-*` blocks in
 * `src/styles/tokens.css`; `cn.test.ts` fails if they drift apart.
 */
export const FONT_SIZE_TOKENS = [
  'micro',
  'tiny',
  'mini',
  'caption',
  'meta',
  'label',
  'body-sm',
  'body',
  'body-lg',
  'base',
  'cta',
  'lead',
  'title-xs',
  'title-sm',
  'title',
  'title-lg',
  'heading-xs',
  'heading-sm',
  'heading-md',
  'heading',
  'display-xs',
  'display-sm',
  'display',
  'stat',
  'stat-lg',
  'stat-xl',
  'stat-2xl',
  'hero',
] as const;

export const SHADOW_TOKENS = [
  'chip',
  'ride',
  'route',
  'search',
  'booking',
  'float',
  'hero',
  'dock',
  'sheet',
  'fab',
  'knob',
] as const;

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: [...FONT_SIZE_TOKENS] }],
      shadow: [{ shadow: [...SHADOW_TOKENS] }],
    },
  },
});

/** Join class names, letting later Tailwind utilities win over earlier ones. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
